import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, CheckCheck, X, Plus, RefreshCw, ArrowUpRight, ArrowDownLeft, Filter,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDate } from "../lib/date";
import PayrollSimulator from "../components/PayrollSimulator";

const money = (n) => `₡${Number(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

function fmt(iso) {
  return formatDate(iso);
}

export default function PayrollAdjustments() {
  const [tab, setTab] = useState("pendientes");
  const [reviews, setReviews] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);

  // Resolve dialog state
  const [openResolve, setOpenResolve] = useState(null);   // review object
  const [action, setAction] = useState("confirm_worked");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [revs, adjs] = await Promise.all([
        api.get("/payroll/reviews", { params: { status: "pending" } }),
        api.get("/payroll/adjustments"),
      ]);
      setReviews(revs.data || []);
      setAdjustments(adjs.data || []);
    } catch {
      toast.error("No se pudo cargar la información");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/payroll/reconcile");
      toast.success(
        `Reconciliación completa: ${data.adjustments_created} ajuste(s), ${data.reviews_created} revisión(es) pendiente(s).`
      );
      await load();
    } catch {
      toast.error("Error en reconciliación");
    } finally {
      setReconciling(false);
    }
  };

  const handleResolve = async () => {
    if (!openResolve) return;
    setSubmitting(true);
    try {
      await api.post(`/payroll/reviews/${openResolve.id}/resolve`, {
        action,
        notes,
        resolved_by: "RRHH",
      });
      toast.success("Pendiente resuelto");
      setOpenResolve(null);
      setAction("confirm_worked");
      setNotes("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al resolver");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAdj = async (id) => {
    if (!window.confirm("¿Eliminar este ajuste pendiente?")) return;
    try {
      await api.delete(`/payroll/adjustments/${id}`);
      toast.success("Ajuste eliminado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  const pendingAdj = adjustments.filter((a) => a.status === "pending");
  const appliedAdj = adjustments.filter((a) => a.status === "applied");

  return (
    <div data-testid="payroll-adjustments-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
            Reconciliación de nómina
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Días proyectados que ya pasaron · Ajustes que se aplicarán en la próxima planilla
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <PayrollSimulator />
          <Button
            variant="outline"
            size="sm"
            onClick={handleReconcile}
            disabled={reconciling}
            data-testid="reconcile-now-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${reconciling ? "animate-spin" : ""}`} />
            Reconciliar ahora
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-zinc-100 mb-5">
          <TabsTrigger value="pendientes" className="text-xs" data-testid="tab-pendientes">
            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Pendientes de revisión ({reviews.length})
          </TabsTrigger>
          <TabsTrigger value="ajustes-pendientes" className="text-xs" data-testid="tab-adjustments-pending">
            <ArrowUpRight className="w-3.5 h-3.5 mr-1.5" /> Ajustes por aplicar ({pendingAdj.length})
          </TabsTrigger>
          <TabsTrigger value="ajustes-aplicados" className="text-xs" data-testid="tab-adjustments-applied">
            <CheckCheck className="w-3.5 h-3.5 mr-1.5" /> Aplicados ({appliedAdj.length})
          </TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="py-16 text-center text-sm text-zinc-400">Cargando…</div>
        ) : tab === "pendientes" ? (
          reviews.length === 0 ? (
            <EmptyState message="Sin pendientes de revisión. Todo está conciliado." />
          ) : (
            <Card className="divide-y divide-zinc-100 overflow-hidden">
              {reviews.map((r) => (
                <div key={r.id} className="p-4 sm:p-5 flex flex-col sm:flex-row gap-3 items-start sm:items-center" data-testid={`review-row-${r.id}`}>
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-5 h-5 text-amber-700" strokeWidth={1.6} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-zinc-900">{r.employee_name}</p>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      No marcó el <span className="font-semibold">{fmt(r.target_date)}</span>.
                      Se proyectaron <span className="font-mono">{r.projected_hours}h</span> ({money(r.projected_pay)}).
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setOpenResolve(r)}
                    className="bg-zinc-900 text-white hover:bg-zinc-800"
                    data-testid={`review-resolve-${r.id}`}
                  >
                    Resolver
                  </Button>
                </div>
              ))}
            </Card>
          )
        ) : tab === "ajustes-pendientes" ? (
          pendingAdj.length === 0 ? (
            <EmptyState message="Sin ajustes pendientes." />
          ) : (
            <AdjustmentsList items={pendingAdj} onDelete={handleDeleteAdj} />
          )
        ) : (
          appliedAdj.length === 0 ? (
            <EmptyState message="Aún no se han aplicado ajustes." />
          ) : (
            <AdjustmentsList items={appliedAdj} readonly />
          )
        )}
      </Tabs>

      <Dialog open={!!openResolve} onOpenChange={(o) => !o && setOpenResolve(null)}>
        <DialogContent className="sm:max-w-md" data-testid="review-resolve-dialog">
          <DialogHeader>
            <DialogTitle>Resolver pendiente</DialogTitle>
            <DialogDescription>
              {openResolve?.employee_name} · {fmt(openResolve?.target_date)} ·
              proyectado: {money(openResolve?.projected_pay)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Acción</Label>
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger data-testid="resolve-action-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirm_worked">
                    ✓ Confirmar que sí trabajó (no se ajusta)
                  </SelectItem>
                  <SelectItem value="incident_created">
                    ⚕ Registré una incidencia (revierte el día proyectado)
                  </SelectItem>
                  <SelectItem value="no_pay">
                    ✕ No se presentó / no pagar (revierte el día proyectado)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notas (opcional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Detalle / motivo"
                data-testid="resolve-notes-input"
              />
            </div>
            {action !== "confirm_worked" && openResolve?.projected_pay > 0 && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                Se creará un ajuste de <strong>{money(-openResolve.projected_pay)}</strong> en la próxima planilla.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenResolve(null)} disabled={submitting}>
              Cancelar
            </Button>
            <Button onClick={handleResolve} disabled={submitting} data-testid="resolve-submit">
              {submitting ? "Guardando…" : "Resolver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <Card className="py-16 px-6 text-center border-dashed">
      <CheckCheck className="w-10 h-10 text-emerald-400 mx-auto mb-4" strokeWidth={1.3} />
      <p className="text-base font-medium text-zinc-700">{message}</p>
    </Card>
  );
}

function AdjustmentsList({ items, onDelete, readonly = false }) {
  return (
    <Card className="divide-y divide-zinc-100 overflow-hidden">
      {items.map((a) => (
        <div
          key={a.id}
          className="p-4 sm:p-5 flex flex-col sm:flex-row gap-3 items-start sm:items-center"
          data-testid={`adjustment-row-${a.id}`}
        >
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
            a.delta_pay < 0 ? "bg-red-100" : "bg-emerald-100"
          }`}>
            {a.delta_pay < 0 ? (
              <ArrowDownLeft className="w-5 h-5 text-red-700" strokeWidth={1.6} />
            ) : (
              <ArrowUpRight className="w-5 h-5 text-emerald-700" strokeWidth={1.6} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-zinc-900">{a.employee_name}</p>
            <p className="text-xs text-zinc-600 mt-0.5">
              {fmt(a.target_date)} · {a.reason}
            </p>
            {a.applied_at && (
              <p className="text-[10px] text-emerald-700 mt-1">
                Aplicado el {formatDate(a.applied_at)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className={`font-mono text-sm font-bold ${a.delta_pay < 0 ? "text-red-600" : "text-emerald-700"}`}>
              {a.delta_pay >= 0 ? "+" : ""}{money(a.delta_pay)}
            </span>
            {!readonly && onDelete && (
              <Button
                size="icon"
                variant="ghost"
                onClick={() => onDelete(a.id)}
                className="h-8 w-8 text-zinc-400 hover:text-red-600"
                data-testid={`adjustment-delete-${a.id}`}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      ))}
    </Card>
  );
}
